import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Pagination,
  Menu,
  MenuItem,
  styled,
  alpha,
  useTheme,
  useMediaQuery,
  Tooltip,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";

const ModernTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 500,
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    transition: "background-color 0.2s ease",
  },
}));

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "600px",
  overflowX: "auto",
}));

function CertificateFields() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [certificateFields, setCertificateFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: "id",
    direction: "desc",
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCertificateField, setSelectedCertificateField] =
    useState(null);
  const [openAddNewCertificateField, setOpenAddNewCertificateField] =
    useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Modal form state
  const [newField, setNewField] = useState("");
  const [saving, setSaving] = useState(false);

  const itemsPerPage = 5;

  useEffect(() => {
    fetchCertificateFields();
  }, []);

  const fetchCertificateFields = async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/support/CertificateFields");
      const data = await res.json();
      const list = data.recordset || data;
      setCertificateFields(list);
    } catch (error) {
      console.error("Error fetching certificate fields:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCertificateFields = certificateFields.filter((item) =>
    item.CertificateField?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const sortedCertificateFields = [...filteredCertificateFields].sort(
    (a, b) => {
      if (sortConfig.key) {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    },
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedCertificateFields.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );

  const handleMenuOpen = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedCertificateField(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCertificateField(null);
  };

  const handleAddNew = () => setOpenAddNewCertificateField(true);

  const handleSaveCertificateField = async () => {
    if (!newField.trim()) return;

    try {
      setSaving(true);
      await fetch("/api/support/CertificateFields", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ CertificateField: newField }),
      });

      setNewField("");
      setOpenAddNewCertificateField(false);
      fetchCertificateFields();
    } catch (error) {
      console.error("Error saving certificate field:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: "white", borderRadius: "16px", p: 3, mb: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Certificate Fields
        </Typography>

        <TextField
          size="small"
          placeholder="Search certificate field..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          sx={{ minWidth: 280 }}
        />

        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
        >
          Add New
        </Button>
      </Box>

      {/* Table */}
      <ResponsiveTableContainer component={Paper}>
        <Table size={isMobile ? "small" : "medium"}>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: theme.palette.primary.light,
                "& th": {
                  color: "white",
                  fontWeight: 600,
                },
              }}
            >
              <ModernTableCell>ID</ModernTableCell>
              <ModernTableCell>Certificate Field</ModernTableCell>
              <ModernTableCell>Action</ModernTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <ModernTableCell colSpan={3} align="center">
                  <CircularProgress size={24} />
                </ModernTableCell>
              </TableRow>
            ) : currentItems.length > 0 ? (
              currentItems.map((item, index) => (
                <StyledTableRow key={item.id}>
                  <ModernTableCell>
                    {indexOfFirstItem + index + 1}
                  </ModernTableCell>
                  <ModernTableCell>{item.CertificateField}</ModernTableCell>
                  <ModernTableCell>
                    <IconButton onClick={(e) => handleMenuOpen(e, item)}>
                      <MoreVertIcon />
                    </IconButton>
                  </ModernTableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <ModernTableCell colSpan={3} align="center">
                  No certificate fields found
                </ModernTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveTableContainer>

      {/* Pagination */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Pagination
          count={Math.ceil(filteredCertificateFields.length / itemsPerPage)}
          page={currentPage}
          onChange={(e, value) => setCurrentPage(value)}
          color="primary"
        />
      </Box>

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMenuClose}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <DeleteIcon fontSize="small" color="error" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      <Dialog
        open={openAddNewCertificateField}
        onClose={() => setOpenAddNewCertificateField(false)}
        maxWidth="xs"
        fullWidth
        sx={{
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: theme.palette.primary.main,
            color: "white",
          }}
        >
          <Typography fontWeight={700}> Add New Certificate Field</Typography>
          <IconButton onClick={() => setOpenAddNewCertificateField(false)} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" color="text.secondary" mb={2}>

          </Typography>

          <TextField
            fullWidth
            label="Certificate Field"
            value={newField}
            onChange={(e) => setNewField(e.target.value)}
            autoFocus
          />
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenAddNewCertificateField(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveCertificateField}
            disabled={!newField.trim() || saving}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CertificateFields;
